import { useState } from 'react';
import { useTenant } from '../../contexts/TenantContext';
import { ErrorState } from '../../components/common';
import { migrationService, type ScopeView, type TuneReport } from '../../services/migration';
import { formatDuration, pct } from './logic';

/** Tune: mine the incumbent's alert history (read-only) for noisy, flappy or ignored alerts. */
export function TunePanel({ scope }: { scope: ScopeView }) {
  const { tenantId } = useTenant();
  const [days, setDays] = useState(30);
  const [report, setReport] = useState<TuneReport | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    if (!tenantId) return;
    setBusy(true);
    setError(null);
    try {
      setReport((await migrationService.tune(tenantId, scope.id, days)).report);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <details open className="card" aria-label="Tune">
      <summary><h3 style={{ display: 'inline' }}>Tune from LogicMonitor’s alert history</h3></summary>
      <p className="text-muted">Read-only. Finds alerts that are loud, clear within minutes, or that nobody acknowledges, and shows what a higher threshold would have removed.</p>
      <div className="form-field-inline">
        <label htmlFor={`tune-days-${scope.id}`}>Look back (days)</label>
        <input id={`tune-days-${scope.id}`} type="number" min={1} max={90} value={days} onChange={(e) => setDays(Number(e.target.value))} />
        <button type="button" className="btn btn-secondary" disabled={busy || !(days >= 1 && days <= 90)} onClick={() => void run()}>Analyse</button>
      </div>
      {error !== null && <ErrorState error={error} />}
      {report && (
        <div aria-label="Tuning report">
          <p>
            {report.alertsAnalysed} alert(s) analysed over {report.sinceDays} days; {report.groupsTotal} datapoint(s) need attention.
            {report.truncated && <> <strong>The history was larger than could be read — the oldest alerts are missing.</strong></>}
          </p>
          {report.groups.length > 0 && (
            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr><th>DataSource / datapoint</th><th>Alerts</th><th>Per device/day</th><th>Median duration</th><th>Acked</th><th>Findings</th><th>Suggestion</th></tr>
                </thead>
                <tbody>
                  {report.groups.map((g, i) => (
                    <tr key={`${g.dataSourceId}-${g.dataPointName}-${i}`}>
                      <td>{g.dataSourceName ?? `DataSource ${g.dataSourceId ?? '?'}`} / {g.dataPointName ?? '?'}{g.threshold && <div className="text-muted">threshold {g.threshold}</div>}</td>
                      <td>{g.alerts} on {g.devices} device(s)</td>
                      <td>{g.alertsPerDeviceDay}</td>
                      <td>{formatDuration(g.medianDurationSeconds)}</td>
                      <td>{pct(g.ackedShare)}</td>
                      <td>{g.flags.join(', ')}{g.notes.length > 0 && <ul>{g.notes.map((n, k) => <li key={k} className="text-muted">{n}</li>)}</ul>}</td>
                      <td>
                        {g.suggestion
                          ? <>raise {g.suggestion.operator} {g.suggestion.current} → {g.suggestion.proposed}: would have removed {pct(g.suggestion.wouldHaveRemoved)} of {g.suggestion.basedOnAlerts} alerts</>
                          : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="text-muted">Suggestions are evidence for a person to apply; nothing is changed.</p>
        </div>
      )}
    </details>
  );
}
